import React, { useState, useEffect } from 'react';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  AlertTitle,
  Tabs,
  Tab,
  Avatar,
  Tooltip,
  IconButton,
  Menu,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Switch,
  FormControlLabel,
  Checkbox,
  InputAdornment,
  Slider,
  Radio,
  RadioGroup,
  Badge,
  Collapse,
  Stack,
  FormGroup,
  FormHelperText,
  CircularProgress,
} from '@mui/material';
import {
  Settings,
  Security,
  Person,
  Groups,
  AdminPanelSettings,
  ManageAccounts,
  Payment,
  DirectionsCar,
  Schedule,
  Cancel,
  AttachMoney,
  Timer,
  Shield,
  Key,
  Email,
  Sms,
  Google,
  Smartphone,
  Lock,
  LockOpen,
  History,
  Warning,
  CheckCircle,
  Error,
  Info,
  Add,
  Edit,
  Delete,
  Save,
  RestartAlt,
  MoreVert,
  ExpandMore,
  ExpandLess,
  Visibility,
  VisibilityOff,
  BookOnline,
  People,
  AccountBalance,
  Rule,
  AccessTime,
  CalendarToday,
  Description,
  VerifiedUser,
  Block,
  Refresh,
  Download,
  Upload,
  Search,
  FilterList,
  Assignment,
  PersonAdd,
  PersonRemove,
  GroupAdd,
  Policy,
  Gavel,
  LocalAtm,
  CreditCard,
  AccountBalanceWallet,
  TrendingUp,
  Speed,
  Star,
  ThumbUp,
  SupportAgent,
  CarRental,
} from '@mui/icons-material';
import { adminStaffService, SystemRole } from '@/services/adminStaffService';
import toast from 'react-hot-toast';

// Use SystemRole from adminStaffService
type Role = SystemRole;

interface Permission {
  id: string;
  resource: string;
  action: string;
  conditions?: string[];
}

interface User {
  id: string;
  name: string;
  email: string;
  role: string;
  department: string;
  avatar?: string;
  lastLogin: string;
  status: 'active' | 'suspended' | 'inactive';
}

interface BookingRule {
  id: string;
  name: string;
  type: 'duration' | 'advance' | 'cancellation' | 'auto-approve';
  value: number;
  unit: 'hours' | 'days' | 'percentage';
  enabled: boolean;
  description: string;
}

interface PayoutRule {
  id: string;
  name: string;
  type: 'host' | 'driver' | 'platform';
  value: number;
  unit: 'hours' | 'percentage' | 'fixed';
  enabled: boolean;
  description: string;
}

interface SLARule {
  id: string;
  name: string;
  type: 'response' | 'upload' | 'trust' | 'suspension';
  value: number;
  unit: 'hours' | 'score' | 'percentage';
  threshold: number;
  enabled: boolean;
  description: string;
}

interface AuthMethod {
  id: string;
  name: string;
  type: 'email' | 'sms' | 'google' | 'authenticator';
  enabled: boolean;
  required2FA: boolean;
  userCount: number;
  lastUpdated: string;
}

interface SecurityLog {
  id: string;
  event: string;
  user: string;
  timestamp: string;
  ip: string;
  location: string;
  device: string;
  status: 'success' | 'failed' | 'blocked';
}

const SettingsRolesPage: React.FC = () => {
  const [selectedTab, setSelectedTab] = useState(0);
  const [selectedRole, setSelectedRole] = useState<Role | null>(null);
  const [roleDialogOpen, setRoleDialogOpen] = useState(false);
  const [userDialogOpen, setUserDialogOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [expandedSections, setExpandedSections] = useState<string[]>(['booking', 'payout', 'sla', 'auth']);

  // API-driven state
  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState(true);

  // Load roles from API
  useEffect(() => {
    loadRoles();
  }, []);

  const loadRoles = async () => {
    setLoading(true);
    try {
      const response = await adminStaffService.getSystemRoles();

      if (response.success && response.data) {
        // Backend returns { data: { roles: [...] } }
        const rolesArray = response.data.roles || [];
        setRoles(rolesArray);
      } else {
        console.error('❌ Failed to load roles:', response.error);
        toast.error('Failed to load roles');
      }
    } catch (error) {
      console.error('❌ Error loading roles:', error);
      toast.error('Failed to load roles');
    } finally {
      setLoading(false);
    }
  };

  // Hardcoded fallback data (removed - using API now)
  const OLD_MOCK_ROLES: Role[] = [
    {
      id: '1',
      name: 'Admin',
      description: 'Full system access with all permissions',
      userCount: 3,
      permissions: [
        { id: '1', resource: 'bookings', actions: ['view', 'create', 'modify', 'delete'] },
        { id: '2', resource: 'users', actions: ['view', 'create', 'modify', 'suspend'] },
        { id: '3', resource: 'payments', actions: ['view', 'process', 'refund', 'adjust'] },
        { id: '4', resource: 'settings', actions: ['view', 'modify', 'configure'] },
      ],
      createdAt: '2023-01-01T00:00:00Z',
      updatedAt: '2024-01-15T10:00:00Z',
      status: 'active',
      type: 'system'
    },
    {
      id: '2',
      name: 'City Manager',
      description: 'Region-specific access with limited permissions',
      userCount: 12,
      permissions: [
        { id: '5', resource: 'bookings', actions: ['view', 'modify'] },
        { id: '6', resource: 'users', actions: ['view', 'modify'] },
        { id: '7', resource: 'payments', actions: ['view'] },
      ],
      createdAt: '2023-02-15T00:00:00Z',
      updatedAt: '2024-01-10T14:30:00Z',
      status: 'active',
      type: 'system'
    },
    {
      id: '3',
      name: 'Agent',
      description: 'Customer support and booking management',
      userCount: 28,
      permissions: [
        { id: '8', resource: 'bookings', actions: ['view', 'modify'] },
        { id: '9', resource: 'users', actions: ['view'] },
      ],
      createdAt: '2023-03-20T00:00:00Z',
      updatedAt: '2024-01-12T09:15:00Z',
      status: 'active',
      type: 'custom'
    },
    {
      id: '4',
      name: 'Finance',
      description: 'Payment processing and financial reports',
      userCount: 7,
      permissions: [
        { id: '10', resource: 'payments', actions: ['view', 'process', 'refund'] },
        { id: '11', resource: 'reports', actions: ['view', 'export'] },
      ],
      createdAt: '2023-04-10T00:00:00Z',
      updatedAt: '2024-01-08T16:45:00Z',
      status: 'active',
      type: 'custom'
    }
  ];

  const permissionMatrix = {
    'Booking Management': {
      'View Bookings': { admin: true, cityManager: true, agent: true, finance: false },
      'Modify Bookings': { admin: true, cityManager: true, agent: true, finance: false },
      'Create Bookings': { admin: true, cityManager: false, agent: false, finance: false },
      'Cancel Bookings': { admin: true, cityManager: false, agent: false, finance: false },
    },
    'User Management': {
      'View Users': { admin: true, cityManager: true, agent: true, finance: false },
      'Create Users': { admin: true, cityManager: false, agent: false, finance: false },
      'Modify Users': { admin: true, cityManager: true, agent: false, finance: false },
      'Suspend Users': { admin: true, cityManager: false, agent: false, finance: false },
    },
    'Financial Operations': {
      'View Payments': { admin: true, cityManager: false, agent: false, finance: true },
      'Process Refunds': { admin: true, cityManager: false, agent: false, finance: true },
      'Adjust Fees': { admin: true, cityManager: false, agent: false, finance: true },
      'Financial Reports': { admin: true, cityManager: false, agent: false, finance: true },
    },
    'System Settings': {
      'Manage Roles': { admin: true, cityManager: false, agent: false, finance: false },
      'View Logs': { admin: true, cityManager: true, agent: false, finance: false },
      'Configure Rules': { admin: true, cityManager: false, agent: false, finance: false },
      'System Configuration': { admin: true, cityManager: false, agent: false, finance: false },
    },
  };

  const bookingRules: BookingRule[] = [
    { id: '1', name: 'Minimum Booking Duration', type: 'duration', value: 1, unit: 'days', enabled: true, description: 'Minimum rental period for vehicles' },
    { id: '2', name: 'Maximum Booking Duration', type: 'duration', value: 30, unit: 'days', enabled: true, description: 'Maximum rental period for vehicles' },
    { id: '3', name: 'Advance Booking Period', type: 'advance', value: 90, unit: 'days', enabled: true, description: 'How far in advance bookings can be made' },
    { id: '4', name: 'Cancellation Deadline', type: 'cancellation', value: 48, unit: 'hours', enabled: true, description: 'Deadline for free cancellation' },
    { id: '5', name: 'Auto-Approve Bookings', type: 'auto-approve', value: 1, unit: 'hours', enabled: false, description: 'Automatically approve bookings after review period' },
  ];

  const payoutRules: PayoutRule[] = [
    { id: '1', name: 'Host Payout Delay', type: 'host', value: 24, unit: 'hours', enabled: true, description: 'Delay before releasing payment to hosts' },
    { id: '2', name: 'Driver Payout Delay', type: 'driver', value: 12, unit: 'hours', enabled: true, description: 'Delay before releasing payment to drivers' },
    { id: '3', name: 'Platform Fee', type: 'platform', value: 15, unit: 'percentage', enabled: true, description: 'Platform commission on bookings' },
    { id: '4', name: 'Minimum Payout Amount', type: 'platform', value: 50, unit: 'fixed', enabled: true, description: 'Minimum amount for payout processing' },
    { id: '5', name: 'Hold Payouts on Dispute', type: 'platform', value: 1, unit: 'hours', enabled: true, description: 'Hold payouts when disputes are raised' },
  ];

  const slaRules: SLARule[] = [
    { id: '1', name: 'Liaison Response Window', type: 'response', value: 6, unit: 'hours', threshold: 95, enabled: true, description: 'Maximum time for agent response' },
    { id: '2', name: 'Document Upload Deadline', type: 'upload', value: 72, unit: 'hours', threshold: 90, enabled: true, description: 'Deadline for document submission' },
    { id: '3', name: 'Max Substitution Rate', type: 'trust', value: 10, unit: 'percentage', threshold: 10, enabled: true, description: 'Maximum allowed vehicle substitution rate' },
    { id: '4', name: 'Trust Score Threshold', type: 'trust', value: 70, unit: 'score', threshold: 100, enabled: true, description: 'Minimum trust score for active status' },
    { id: '5', name: 'Auto-Suspend Low Trust', type: 'suspension', value: 50, unit: 'score', threshold: 100, enabled: true, description: 'Auto-suspend users below trust threshold' },
  ];

  const authMethods: AuthMethod[] = [
    { id: '1', name: 'Email & Password', type: 'email', enabled: true, required2FA: false, userCount: 2847, lastUpdated: '2024-01-15T10:00:00Z' },
    { id: '2', name: 'SMS Verification', type: 'sms', enabled: true, required2FA: false, userCount: 1523, lastUpdated: '2024-01-14T15:30:00Z' },
    { id: '3', name: 'Google Authentication', type: 'google', enabled: true, required2FA: false, userCount: 892, lastUpdated: '2024-01-13T09:45:00Z' },
    { id: '4', name: 'Authenticator App', type: 'authenticator', enabled: true, required2FA: true, userCount: 156, lastUpdated: '2024-01-12T14:20:00Z' },
  ];

  const securityLogs: SecurityLog[] = [
    { id: '1', event: 'Max Failed Login Attempts', user: 'sarah.johnson@polarride.com', timestamp: '2024-01-15T10:45:00Z', ip: '192.168.1.100', location: 'Lagos, Nigeria', device: 'Chrome/Windows', status: 'blocked' },
    { id: '2', event: 'Account Lockout Duration', user: 'michael.chen@polarride.com', timestamp: '2024-01-15T09:30:00Z', ip: '10.0.0.50', location: 'Nairobi, Kenya', device: 'Safari/Mac', status: 'failed' },
    { id: '3', event: 'Password Expiry', user: 'alex.rodriguez@polarride.com', timestamp: '2024-01-14T16:20:00Z', ip: '172.16.0.25', location: 'Accra, Ghana', device: 'Mobile App/iOS', status: 'success' },
    { id: '4', event: 'IP-Based Restrictions', user: 'jamie.wilson@polarride.com', timestamp: '2024-01-14T11:15:00Z', ip: '203.0.113.45', location: 'Unknown', device: 'Firefox/Linux', status: 'blocked' },
    { id: '5', event: 'Recent Password Reset Logs', user: 'various', timestamp: '2024-01-14T08:00:00Z', ip: 'Multiple', location: 'Various', device: 'Various', status: 'success' },
  ];

  const handleToggleSection = (section: string) => {
    setExpandedSections(prev =>
      prev.includes(section)
        ? prev.filter(s => s !== section)
        : [...prev, section]
    );
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status: string): string => {
    switch (status) {
      case 'active': return '#4CAF50';
      case 'inactive': return '#FF9800';
      case 'suspended': return '#F44336';
      case 'success': return '#4CAF50';
      case 'failed': return '#FF9800';
      case 'blocked': return '#F44336';
      default: return '#9E9E9E';
    }
  };

  const getPermissionIcon = (resource: string): React.ReactNode => {
    switch (resource) {
      case 'bookings': return <BookOnline />;
      case 'users': return <People />;
      case 'payments': return <Payment />;
      case 'settings': return <Settings />;
      case 'reports': return <Description />;
      default: return <Assignment />;
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            Settings & Roles
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage access, rules, and authentication for the PolarRide platform
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button variant="outlined" startIcon={<RestartAlt />}>
            Reset to Default
          </Button>
          <Button variant="contained" startIcon={<Save />}>
            Save Changes
          </Button>
        </Box>
      </Box>

      {/* Tabs Navigation */}
      <Card sx={{ mb: 3 }}>
        <Tabs value={selectedTab} onChange={(e, newValue) => setSelectedTab(newValue)} variant="scrollable" scrollButtons="auto">
          <Tab icon={<Groups />} label="Roles & Permissions" />
          <Tab icon={<Rule />} label="Rule Engine" />
          <Tab icon={<Key />} label="2FA & Login Control" />
          <Tab icon={<History />} label="Audit Logs" />
        </Tabs>
      </Card>

      {/* Tab Content */}
      {selectedTab === 0 && (
        <Grid container spacing={3}>
          {/* Role Management */}
          <Grid item xs={12} md={5}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                  <Typography variant="h6">Role Management</Typography>
                  <Button size="small" variant="contained" startIcon={<Add />} onClick={() => setRoleDialogOpen(true)}>
                    Create Role
                  </Button>
                </Box>

                {loading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress />
                  </Box>
                ) : roles.length === 0 ? (
                  <Alert severity="info">
                    No roles found. Click "Create Role" to add a new role.
                  </Alert>
                ) : (
                  <List>
                    {roles.map((role) => (
                    <ListItem
                      key={role.id}
                      sx={{
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 2,
                        mb: 2,
                        cursor: 'pointer',
                        backgroundColor: selectedRole?.id === role.id ? 'action.selected' : 'background.paper',
                        '&:hover': { backgroundColor: 'action.hover' }
                      }}
                      onClick={() => setSelectedRole(role)}
                    >
                      <ListItemIcon>
                        <Avatar sx={{ bgcolor: 'primary.main', width: 48, height: 48 }}>
                          <AdminPanelSettings />
                        </Avatar>
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="subtitle1">{role.name}</Typography>
                            {role.type === 'system' && (
                              <Chip label="System" size="small" color="primary" />
                            )}
                          </Box>
                        }
                        secondary={
                          <Box>
                            <Typography variant="body2" color="text.secondary">
                              {role.description}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {role.userCount} Users
                            </Typography>
                          </Box>
                        }
                      />
                      <ListItemSecondaryAction>
                        <IconButton edge="end">
                          <MoreVert />
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                    ))}
                  </List>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Permission Matrix */}
          <Grid item xs={12} md={7}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Permission Matrix</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
                  Super Admin has all permissions by default and is excluded from this matrix
                </Typography>

                {loading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress />
                  </Box>
                ) : roles.length === 0 ? (
                  <Alert severity="info">
                    No roles available to display permissions.
                  </Alert>
                ) : (
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Permission</TableCell>
                          {roles
                            .filter((role) => role.id !== 'super_admin')
                            .map((role) => (
                              <TableCell key={role.id} align="center" sx={{ textTransform: 'uppercase' }}>
                                {role.name}
                              </TableCell>
                            ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {Object.entries(permissionMatrix).map(([category, permissions]) => (
                          <React.Fragment key={category}>
                            <TableRow>
                              <TableCell
                                colSpan={roles.filter((r) => r.id !== 'super_admin').length + 1}
                                sx={{ bgcolor: 'action.hover', fontWeight: 600 }}
                              >
                                {category}
                              </TableCell>
                            </TableRow>
                            {Object.entries(permissions).map(([permission, rolePermissions]) => (
                              <TableRow key={permission}>
                                <TableCell sx={{ pl: 4 }}>{permission}</TableCell>
                                {roles
                                  .filter((role) => role.id !== 'super_admin')
                                  .map((role) => (
                                    <TableCell key={role.id} align="center">
                                      <Checkbox
                                        checked={rolePermissions[role.id] || false}
                                        color="primary"
                                      />
                                    </TableCell>
                                  ))}
                              </TableRow>
                            ))}
                          </React.Fragment>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {selectedTab === 1 && (
        <Grid container spacing={3}>
          {/* Booking Rules */}
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CarRental />
                    <Typography variant="h6">Booking Rules</Typography>
                  </Box>
                  <IconButton onClick={() => handleToggleSection('booking')}>
                    {expandedSections.includes('booking') ? <ExpandLess /> : <ExpandMore />}
                  </IconButton>
                </Box>

                <Collapse in={expandedSections.includes('booking')}>
                  <List>
                    {bookingRules.map((rule) => (
                      <ListItem key={rule.id} sx={{ px: 0 }}>
                        <ListItemText
                          primary={rule.name}
                          secondary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                              <TextField
                                size="small"
                                type="number"
                                value={rule.value}
                                sx={{ width: 100 }}
                                InputProps={{
                                  endAdornment: <InputAdornment position="end">{rule.unit}</InputAdornment>
                                }}
                              />
                              <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
                                {rule.description}
                              </Typography>
                            </Box>
                          }
                        />
                        <ListItemSecondaryAction>
                          <Switch checked={rule.enabled} />
                        </ListItemSecondaryAction>
                      </ListItem>
                    ))}
                  </List>
                </Collapse>
              </CardContent>
            </Card>
          </Grid>

          {/* Payout Rules */}
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Payment />
                    <Typography variant="h6">Payout Rules</Typography>
                  </Box>
                  <IconButton onClick={() => handleToggleSection('payout')}>
                    {expandedSections.includes('payout') ? <ExpandLess /> : <ExpandMore />}
                  </IconButton>
                </Box>

                <Collapse in={expandedSections.includes('payout')}>
                  <List>
                    {payoutRules.map((rule) => (
                      <ListItem key={rule.id} sx={{ px: 0 }}>
                        <ListItemText
                          primary={rule.name}
                          secondary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                              <TextField
                                size="small"
                                type="number"
                                value={rule.value}
                                sx={{ width: 100 }}
                                InputProps={{
                                  endAdornment: <InputAdornment position="end">
                                    {rule.unit === 'percentage' ? '%' : rule.unit === 'fixed' ? '$' : rule.unit}
                                  </InputAdornment>
                                }}
                              />
                              <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
                                {rule.description}
                              </Typography>
                            </Box>
                          }
                        />
                        <ListItemSecondaryAction>
                          <Switch checked={rule.enabled} />
                        </ListItemSecondaryAction>
                      </ListItem>
                    ))}
                  </List>
                </Collapse>
              </CardContent>
            </Card>
          </Grid>

          {/* SLA & Trust Rules */}
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Speed />
                    <Typography variant="h6">SLA & Trust Rules</Typography>
                  </Box>
                  <IconButton onClick={() => handleToggleSection('sla')}>
                    {expandedSections.includes('sla') ? <ExpandLess /> : <ExpandMore />}
                  </IconButton>
                </Box>

                <Collapse in={expandedSections.includes('sla')}>
                  <List>
                    {slaRules.map((rule) => (
                      <ListItem key={rule.id} sx={{ px: 0 }}>
                        <ListItemText
                          primary={rule.name}
                          secondary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                              <TextField
                                size="small"
                                type="number"
                                value={rule.value}
                                sx={{ width: 80 }}
                              />
                              <Typography variant="caption" color="text.secondary">
                                {rule.unit === 'score' ? `/ ${rule.threshold}` : rule.unit}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
                                {rule.description}
                              </Typography>
                            </Box>
                          }
                        />
                        <ListItemSecondaryAction>
                          <Switch checked={rule.enabled} />
                        </ListItemSecondaryAction>
                      </ListItem>
                    ))}
                  </List>
                </Collapse>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {selectedTab === 2 && (
        <Grid container spacing={3}>
          {/* Authentication Methods */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Authentication Methods</Typography>
                
                <List>
                  {authMethods.map((method) => (
                    <ListItem key={method.id} sx={{ px: 0 }}>
                      <ListItemIcon>
                        {method.type === 'email' && <Email color="primary" />}
                        {method.type === 'sms' && <Sms color="primary" />}
                        {method.type === 'google' && <Google color="primary" />}
                        {method.type === 'authenticator' && <Smartphone color="primary" />}
                      </ListItemIcon>
                      <ListItemText
                        primary={method.name}
                        secondary={
                          <Box>
                            <Typography variant="body2" color="text.secondary">
                              {method.userCount.toLocaleString()} users
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Last updated: {formatDate(method.lastUpdated)}
                            </Typography>
                          </Box>
                        }
                      />
                      <ListItemSecondaryAction sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <FormControlLabel
                          control={<Checkbox checked={method.required2FA} size="small" />}
                          label="2FA"
                          labelPlacement="start"
                        />
                        <Switch checked={method.enabled} />
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>

                <Divider sx={{ my: 2 }} />

                <Typography variant="subtitle2" gutterBottom>2FA Requirements</Typography>
                <FormGroup>
                  <FormControlLabel control={<Checkbox defaultChecked />} label="Required for all users" />
                  <FormControlLabel control={<Checkbox />} label="Required for admin roles only" />
                  <FormControlLabel control={<Checkbox />} label="Optional for all users" />
                </FormGroup>
              </CardContent>
            </Card>
          </Grid>

          {/* Login Security & Reset Logs */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Login Security & Reset Logs</Typography>
                
                <List>
                  <ListItem sx={{ px: 0 }}>
                    <ListItemText
                      primary="Max Failed Login Attempts"
                      secondary="Number of attempts before account lockout"
                    />
                    <TextField
                      size="small"
                      type="number"
                      defaultValue={5}
                      sx={{ width: 100 }}
                      InputProps={{
                        endAdornment: <InputAdornment position="end">attempts</InputAdornment>
                      }}
                    />
                  </ListItem>

                  <ListItem sx={{ px: 0 }}>
                    <ListItemText
                      primary="Account Lockout Duration"
                      secondary="Time before automatic unlock"
                    />
                    <TextField
                      size="small"
                      type="number"
                      defaultValue={30}
                      sx={{ width: 100 }}
                      InputProps={{
                        endAdornment: <InputAdornment position="end">minutes</InputAdornment>
                      }}
                    />
                  </ListItem>

                  <ListItem sx={{ px: 0 }}>
                    <ListItemText
                      primary="Password Expiry"
                      secondary="Days before password must be changed"
                    />
                    <TextField
                      size="small"
                      type="number"
                      defaultValue={90}
                      sx={{ width: 100 }}
                      InputProps={{
                        endAdornment: <InputAdornment position="end">days</InputAdornment>
                      }}
                    />
                  </ListItem>

                  <ListItem sx={{ px: 0 }}>
                    <ListItemText
                      primary="IP-Based Restrictions"
                      secondary="Restrict login to specific IP ranges"
                    />
                    <Switch defaultChecked />
                  </ListItem>
                </List>

                <Divider sx={{ my: 2 }} />

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="subtitle2">Recent Password Reset Logs</Typography>
                  <Button size="small" startIcon={<Visibility />}>View All</Button>
                </Box>

                <List dense>
                  {securityLogs.slice(0, 3).map((log) => (
                    <ListItem key={log.id} sx={{ px: 0 }}>
                      <ListItemText
                        primary={log.user}
                        secondary={`${log.event} • ${formatDate(log.timestamp)}`}
                      />
                      <Chip
                        label={log.status}
                        size="small"
                        sx={{
                          backgroundColor: getStatusColor(log.status) + '20',
                          color: getStatusColor(log.status)
                        }}
                      />
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {selectedTab === 3 && (
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6">Security Audit Logs</Typography>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  size="small"
                  placeholder="Search logs..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: <Search sx={{ color: 'text.secondary', mr: 1 }} />
                  }}
                />
                <Button variant="outlined" startIcon={<Download />}>
                  Export
                </Button>
              </Box>
            </Box>

            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Event</TableCell>
                    <TableCell>User</TableCell>
                    <TableCell>Timestamp</TableCell>
                    <TableCell>IP Address</TableCell>
                    <TableCell>Location</TableCell>
                    <TableCell>Device</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {securityLogs.map((log) => (
                    <TableRow key={log.id}>
                      <TableCell>{log.event}</TableCell>
                      <TableCell>{log.user}</TableCell>
                      <TableCell>{formatDate(log.timestamp)}</TableCell>
                      <TableCell>{log.ip}</TableCell>
                      <TableCell>{log.location}</TableCell>
                      <TableCell>{log.device}</TableCell>
                      <TableCell>
                        <Chip
                          label={log.status}
                          size="small"
                          sx={{
                            backgroundColor: getStatusColor(log.status) + '20',
                            color: getStatusColor(log.status)
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {/* Dialogs */}
      <Dialog open={roleDialogOpen} onClose={() => setRoleDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Create New Role</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Role Name"
            margin="normal"
            variant="outlined"
          />
          <TextField
            fullWidth
            label="Description"
            margin="normal"
            variant="outlined"
            multiline
            rows={3}
          />
          <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
            Select Permissions
          </Typography>
          <FormGroup>
            <FormControlLabel control={<Checkbox />} label="Booking Management" />
            <FormControlLabel control={<Checkbox />} label="User Management" />
            <FormControlLabel control={<Checkbox />} label="Financial Operations" />
            <FormControlLabel control={<Checkbox />} label="System Settings" />
          </FormGroup>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRoleDialogOpen(false)}>Cancel</Button>
          <Button variant="contained">Create Role</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SettingsRolesPage;